import React from "react";
import { PanResponder, Platform, useWindowDimensions } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { View } from "native-base";
import { usePersistedState } from "../../Universal/API/Context-API/hooks/usePersistedState";
import { formatOrderHistoryDate } from "../helpers";
import CollapsedButton from "./CartHistoryFloating/CollapsedButton";
import Controls from "./CartHistoryFloating/Controls";
import CurrentOrderCard from "./CartHistoryFloating/CurrentOrderCard";
import EmptyState from "./CartHistoryFloating/EmptyState";
import {
  clamp,
  createDefaultState,
  getOrderCardTotal,
  STORAGE_KEY,
} from "./CartHistoryFloating/helpers";
import OrderCard from "./CartHistoryFloating/OrderCard";
import PanelHeader from "./CartHistoryFloating/PanelHeader";
import ResizeHandle from "./CartHistoryFloating/ResizeHandle";
import SectionTitle from "./CartHistoryFloating/SectionTitle";
import type {
  CartHistoryFloatingPanelProps,
  FloatingPanelState,
  HistoryStatusFilter,
} from "./CartHistoryFloating/types";

const CartHistoryFloatingPanel: React.FC<CartHistoryFloatingPanelProps> = ({
  scaleAll,
  orders,
  showDiscountPrices,
  selectedOrderId,
  isHistoryMode,
  currentOrderQuantity,
  currentOrderItemsCount,
  currentOrderTotal,
  onOpenCurrentCart,
  onOpenHistoryOrder,
}) => {
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const [storedState, setStoredState] = usePersistedState<FloatingPanelState>({
    key: STORAGE_KEY,
    initial: createDefaultState({ viewportWidth, viewportHeight, scaleAll }),
  });
  const [panelState, setPanelState] = React.useState<FloatingPanelState>(() =>
    storedState ??
    createDefaultState({
      viewportWidth,
      viewportHeight,
      scaleAll,
    })
  );

  const minWidth = 300 * scaleAll;
  const maxWidth = Math.max(minWidth, viewportWidth - 32 * scaleAll);
  const minHeight = 220 * scaleAll;
  const maxHeight = Math.max(minHeight, viewportHeight - 40 * scaleAll);
  const collapsedTop = 96 * scaleAll;
  const collapsedRight = 24 * scaleAll;
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<HistoryStatusFilter>("all");
  const panelStateRef = React.useRef(panelState);

  React.useEffect(() => {
    panelStateRef.current = panelState;
  }, [panelState]);

  React.useEffect(() => {
    if (!storedState) {
      return;
    }

    setPanelState((prev) => ({
      ...prev,
      ...storedState,
    }));
  }, [storedState]);

  React.useEffect(() => {
    setPanelState((prev) => {
      const nextWidth = clamp(prev.width, minWidth, maxWidth);
      const nextHeight = clamp(prev.height, minHeight, maxHeight);
      const maxX = Math.max(8 * scaleAll, viewportWidth - nextWidth - 8 * scaleAll);
      const maxY = Math.max(8 * scaleAll, viewportHeight - nextHeight - 8 * scaleAll);

      return {
        ...prev,
        width: nextWidth,
        height: nextHeight,
        x: clamp(prev.x, 8 * scaleAll, maxX),
        y: clamp(prev.y, 8 * scaleAll, maxY),
      };
    });
  }, [maxHeight, maxWidth, minHeight, minWidth, scaleAll, viewportHeight, viewportWidth]);

  const persistPanelState = React.useCallback(
    (nextState: FloatingPanelState) => {
      setStoredState(nextState);
    },
    [setStoredState]
  );

  const dragStateRef = React.useRef<{
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
  } | null>(null);

  const resizeStateRef = React.useRef<{
    startX: number;
    startY: number;
    initialWidth: number;
    initialHeight: number;
  } | null>(null);

  const dragResponder = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: (evt) => {
          dragStateRef.current = {
            startX: evt.nativeEvent.pageX,
            startY: evt.nativeEvent.pageY,
            initialX: panelState.x,
            initialY: panelState.y,
          };
        },
        onPanResponderMove: (evt) => {
          const dragState = dragStateRef.current;
          if (!dragState) return;

          const nextX = dragState.initialX + (evt.nativeEvent.pageX - dragState.startX);
          const nextY = dragState.initialY + (evt.nativeEvent.pageY - dragState.startY);
          const maxX = Math.max(8 * scaleAll, viewportWidth - panelState.width - 8 * scaleAll);
          const maxY = Math.max(8 * scaleAll, viewportHeight - panelState.height - 8 * scaleAll);

          setPanelState((prev) => ({
            ...prev,
            x: clamp(nextX, 8 * scaleAll, maxX),
            y: clamp(nextY, 8 * scaleAll, maxY),
          }));
        },
        onPanResponderRelease: () => {
          dragStateRef.current = null;
          persistPanelState(panelStateRef.current);
        },
        onPanResponderTerminate: () => {
          dragStateRef.current = null;
          persistPanelState(panelStateRef.current);
        },
      }),
    [panelState, persistPanelState, scaleAll, viewportHeight, viewportWidth]
  );

  const resizeResponder = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: (evt) => {
          resizeStateRef.current = {
            startX: evt.nativeEvent.pageX,
            startY: evt.nativeEvent.pageY,
            initialWidth: panelState.width,
            initialHeight: panelState.height,
          };
        },
        onPanResponderMove: (evt) => {
          const resizeState = resizeStateRef.current;
          if (!resizeState) return;

          const nextWidth = resizeState.initialWidth + (evt.nativeEvent.pageX - resizeState.startX);
          const nextHeight =
            resizeState.initialHeight + (evt.nativeEvent.pageY - resizeState.startY);

          setPanelState((prev) => ({
            ...prev,
            width: clamp(nextWidth, minWidth, maxWidth),
            height: clamp(nextHeight, minHeight, maxHeight),
          }));
        },
        onPanResponderRelease: () => {
          resizeStateRef.current = null;
          persistPanelState(panelStateRef.current);
        },
        onPanResponderTerminate: () => {
          resizeStateRef.current = null;
          persistPanelState(panelStateRef.current);
        },
      }),
    [maxHeight, maxWidth, minHeight, minWidth, panelState, persistPanelState]
  );

  const handleCollapse = React.useCallback(() => {
    const nextState = {
      ...panelState,
      collapsed: true,
    };
    setPanelState(nextState);
    persistPanelState(nextState);
  }, [panelState, persistPanelState]);

  const handleExpand = React.useCallback(() => {
    const nextState = {
      ...panelState,
      collapsed: false,
    };
    setPanelState(nextState);
    persistPanelState(nextState);
  }, [panelState, persistPanelState]);

  if (!orders.length) {
    return null;
  }

  const filteredOrders = orders.filter((order) => {
    const matchesStatus = statusFilter === "all" ? true : order.status === statusFilter;
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!matchesStatus) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const haystack = [
      order.order_id,
      order.order_name,
      formatOrderHistoryDate(order.updated_at || order.created_at),
      String(getOrderCardTotal({ order, showDiscountPrices })),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(normalizedQuery);
  });

  if (panelState.collapsed) {
    return (
      <CollapsedButton
        scaleAll={scaleAll}
        top={collapsedTop}
        right={collapsedRight}
        onPress={handleExpand}
      />
    );
  }

  return (
    <View
      style={{
        position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
        left: panelState.x,
        top: panelState.y,
        width: panelState.width,
        height: panelState.height,
        zIndex: 40,
      }}
    >
      <View
        style={{
          flex: 1,
          borderRadius: 26 * scaleAll,
          backgroundColor: "#FFFFFF",
          borderWidth: 1,
          borderColor: "#ECE8E1",
          shadowColor: "#000",
          shadowOpacity: 0.12,
          shadowRadius: 24,
          shadowOffset: { width: 0, height: 14 },
          elevation: 12,
          overflow: "hidden",
        }}
      >
        <PanelHeader
          scaleAll={scaleAll}
          onCollapse={handleCollapse}
          panHandlers={dragResponder.panHandlers}
        />

        <View
          style={{
            flex: 1,
            paddingHorizontal: 14 * scaleAll,
          }}
        >
          <FlashList
            data={filteredOrders}
            keyExtractor={(item) => item.order_id}
            estimatedItemSize={108 * scaleAll}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingTop: 14 * scaleAll,
              paddingBottom: 24 * scaleAll,
            }}
            ListHeaderComponent={
              <View>
                <Controls
                  scaleAll={scaleAll}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                />

                <SectionTitle scaleAll={scaleAll} text="Новый заказ" />

                <CurrentOrderCard
                  scaleAll={scaleAll}
                  isHistoryMode={isHistoryMode}
                  currentOrderQuantity={currentOrderQuantity}
                  currentOrderItemsCount={currentOrderItemsCount}
                  currentOrderTotal={currentOrderTotal}
                  onPress={onOpenCurrentCart}
                />

                <SectionTitle scaleAll={scaleAll} text="Архив" marginTop={4 * scaleAll} />
              </View>
            }
            ListEmptyComponent={<EmptyState scaleAll={scaleAll} />}
            renderItem={({ item }) => {
              const isSelected = isHistoryMode && selectedOrderId === item.order_id;

              return (
                <OrderCard
                  scaleAll={scaleAll}
                  order={item}
                  showDiscountPrices={showDiscountPrices}
                  isSelected={isSelected}
                  onPress={() => onOpenHistoryOrder(item.order_id)}
                />
              );
            }}
          />
        </View>

        <ResizeHandle scaleAll={scaleAll} panHandlers={resizeResponder.panHandlers} />
      </View>
    </View>
  );
};

export default React.memo(CartHistoryFloatingPanel);
